import Page from "src/components/Page";
import { FC, useEffect } from "react";
import { Box, Card, CircularProgress, Container, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCompanyDetails } from "src/modules/company/hooks/useCompanyDetails";
import { CompanyType } from "src/modules/company/types/Company";
import { FieldValues, useForm } from "react-hook-form";
import { DEFAULT_FORM_BEHAVIOUR } from "src/config/config";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider } from "src/components/hook-form";
import UpsertCompanyDetailsForm, {
    UpsertCompanyDetailsFormSchema,
} from "src/modules/company/components/forms/UpsertCompanyDetailsForm";
import { userCompanyDataMutation } from "src/modules/company/hooks/userCompanyDataMutation";
import { useSnackbar } from "notistack";
import { useProfileUserPermissions } from "src/modules/profile/hooks/Profile/useUserPermissions";

const ConfigurationUpsertPage: FC = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { data: companyData, isLoading } = useCompanyDetails();
    const { data: permissions } = useProfileUserPermissions();
    const companyType = permissions?.companyType || null;

    const methods = useForm({
        ...DEFAULT_FORM_BEHAVIOUR,
        defaultValues: companyData
            ? {
                  name: companyData.invoiceData?.name ?? "",
                  vatId: companyData.invoiceData?.vatId ?? "",
                  address: {
                      address1: companyData.invoiceData?.address?.address1 ?? "",
                      address2: companyData.invoiceData?.address?.address2 ?? "",
                      city: companyData.invoiceData?.address?.city ?? "",
                      postalCode: companyData.invoiceData?.address?.postalCode ?? "",
                  },
                  eipaUsername: companyData.eipaUsername ?? "",
                  eipaPassword: "",
                  iWantInvoice: !!companyData.invoiceData?.vatId,
              }
            : {
                  name: "",
                  vatId: "",
                  address: {
                      address1: "",
                      address2: "",
                      city: "",
                      postalCode: "",
                  },
                  eipaUsername: "",
                  eipaPassword: "",
                  iWantInvoice: false,
              },
        resolver: yupResolver(UpsertCompanyDetailsFormSchema(t, companyType)),
    });

    const { mutate: updateCompanyData } = userCompanyDataMutation({
        onSuccess: () => {
            enqueueSnackbar(t("shared.saved"));
        },
        onError: (errors) => {
            Object.keys(errors).forEach((key) => {
                methods.setError(key as any, {
                    type: "custom",
                    message: t("shared.validation.api." + `${errors[key]}`),
                });
            });
        },
    });

    const onSubmit = async (data: FieldValues) => {
        const shouldSendInvoiceData =
            companyType !== CompanyType.HOME ||
            (companyType === CompanyType.HOME && !!data.iWantInvoice);
        const isHomeCompany = companyType === CompanyType.HOME;
        const normalizedEipaUsername = isHomeCompany
            ? null
            : data.eipaUsername && data.eipaUsername.trim()
              ? data.eipaUsername
              : null;
        const normalizedEipaPassword = isHomeCompany
            ? null
            : data.eipaPassword && data.eipaPassword.trim()
              ? data.eipaPassword
              : null;
        const hasExistingEipaPassword = !!companyData?.eipaPasswordSet;
        const isEipaPasswordFieldModified = !!methods.formState.dirtyFields?.eipaPassword;
        const shouldUpdateEipaPassword =
            !isHomeCompany &&
            (normalizedEipaPassword !== null ||
                (hasExistingEipaPassword && isEipaPasswordFieldModified));

        updateCompanyData({
            name: data.name,
            vatId: shouldSendInvoiceData && data.vatId ? data.vatId : null,
            address:
                shouldSendInvoiceData &&
                data.address?.address1 &&
                data.address?.city &&
                data.address?.postalCode
                    ? {
                          address1: data.address.address1,
                          address2: data.address.address2 || null,
                          city: data.address.city,
                          postalCode: data.address.postalCode,
                      }
                    : null,
            eipaUsername: normalizedEipaUsername,
            eipaPassword: normalizedEipaPassword,
            updateEipaPassword: shouldUpdateEipaPassword,
        });
    };

    useEffect(() => {
        if (companyData) {
            methods.reset({
                name: companyData.invoiceData?.name ?? "",
                vatId: companyData.invoiceData?.vatId ?? "",
                address: {
                    address1: companyData.invoiceData?.address?.address1 ?? "",
                    address2: companyData.invoiceData?.address?.address2 ?? "",
                    city: companyData.invoiceData?.address?.city ?? "",
                    postalCode: companyData.invoiceData?.address?.postalCode ?? "",
                },
                eipaUsername: companyData.eipaUsername ?? "",
                eipaPassword: "",
                iWantInvoice: !!companyData.invoiceData?.vatId,
            });
        }
    }, [companyData]);

    if (isLoading || !companyType) {
        return (
            <Page title={t("configuration.title")}>
                <Container maxWidth={"xl"}>
                    <Card variant="main">
                        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                            <CircularProgress />
                        </Box>
                    </Card>
                </Container>
            </Page>
        );
    }

    return (
        <Page title={t("configuration.title")}>
            <Container maxWidth={"xl"}>
                <Box
                    sx={{
                        mb: { xs: 2, sm: 4 },
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "center" },
                    }}
                >
                    <Typography variant="h3" component="h1">
                        {t("configuration.title")}
                    </Typography>
                </Box>
                <Card variant="main">
                    <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
                        <UpsertCompanyDetailsForm />
                    </FormProvider>
                </Card>
            </Container>
        </Page>
    );
};

export default ConfigurationUpsertPage;
